import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";

const NAV = [
  { to: "/jee", label: "JEE" },
  { to: "/neet", label: "NEET" },
  { to: "/olympiads", label: "Olympiads" },
  { to: "/govt-exams", label: "Govt Exams" },
  { to: "/features", label: "Features" },
  { to: "/pricing", label: "Pricing" },
  { to: "/blog", label: "Blog" },
];

export default function MarketingLayout() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <div data-testid="marketing-layout" className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-link">
            <div className="h-9 w-9 rounded-xl bg-primary text-primary-foreground grid place-items-center font-display font-bold">M</div>
            <div className="leading-none">
              <div className="font-display font-bold text-lg tracking-tight">Mock Test Club</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">AI CBT · JEE · NEET · More</div>
            </div>
          </Link>
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`marketing-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                    isActive ? "bg-muted text-foreground" : "text-foreground/70 hover:text-foreground hover:bg-muted/60"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-2 shrink-0">
            <Link to="/login"><Button data-testid="header-signin" variant="ghost" className="rounded-full">Sign in</Button></Link>
            <Link to="/signup"><Button data-testid="header-start-free" className="rounded-full">Start free mock</Button></Link>
          </div>
          <button data-testid="mobile-menu-btn" className="lg:hidden p-2" onClick={() => setOpen((v) => !v)} aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-border bg-background">
            <div className="px-4 py-3 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} className={({isActive}) => `rounded-lg px-3 py-2 text-sm font-medium ${isActive ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                  {n.label}
                </NavLink>
              ))}
              <div className="pt-2 flex gap-2">
                <Link to="/login" className="flex-1"><Button variant="outline" className="w-full rounded-full">Sign in</Button></Link>
                <Link to="/signup" className="flex-1"><Button className="w-full rounded-full">Start free</Button></Link>
              </div>
            </div>
          </div>
        )}
      </header>
      <main className="flex-1"><Outlet /></main>
      <footer className="border-t border-border bg-card/40">
        <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="h-8 w-8 rounded-lg bg-primary text-primary-foreground grid place-items-center font-display font-bold">M</div>
              <span className="font-display font-bold">Mock Test Club</span>
            </div>
            <p className="text-sm text-muted-foreground max-w-xs">AI-powered CBT practice for JEE, NEET, Olympiads and competitive exams — used by aspirants across India.</p>
          </div>
          <FooterCol title="Exams" links={[
            {to: "/jee", label: "JEE Main & Advanced"},
            {to: "/neet", label: "NEET UG"},
            {to: "/olympiads", label: "Olympiads"},
            {to: "/govt-exams", label: "SSC / Banking / Govt"},
          ]}/>
          <FooterCol title="Product" links={[
            {to: "/features", label: "Features"},
            {to: "/how-it-works", label: "How it works"},
            {to: "/pricing", label: "Pricing"},
            {to: "/results", label: "Results & testimonials"},
          ]}/>
          <FooterCol title="Company" links={[
            {to: "/faq", label: "FAQ"},
            {to: "/contact", label: "Contact"},
            {to: "/cities", label: "Cities we serve"},
            {to: "/blog", label: "Blog"},
          ]}/>
        </div>
        <div className="border-t border-border">
          <div className="max-w-7xl mx-auto px-6 py-5 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
            <div>© 2026 Mock Test Club — Serious CBT practice, joyfully delivered.</div>
            <div>Made in India · Trusted by aspirants in 12+ cities</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-foreground/60 mb-3">{title}</div>
      <ul className="space-y-2">
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className="text-sm text-foreground/80 hover:text-primary transition-colors duration-200">{l.label}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
